import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/new-components/ui/button';
import { authApi } from '../services/api';
import { BRAND_ASSETS, TENANT } from '../config';
import { InlineError } from '../components/Auth/InlineError';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!email.trim() || !email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.forgotPassword(email.trim());
      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.message || 'Unable to send reset instructions right now.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full px-4 py-4 text-lg rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none transition-colors ${
    error ? 'border-destructive focus:border-destructive' : 'border-border focus:border-primary'
  }`;

  return (
    <div className="min-h-screen bg-background flex flex-col portal-typography">
      {/* Header — back + logo, matching AuthFlow */}
      <div className="flex items-center justify-between px-4 md:px-6 py-4 md:py-6">
        <Link
          to="/login"
          aria-label="Back to sign in"
          className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src={BRAND_ASSETS.logo.primary} alt={TENANT.branding.name} className="h-11 object-contain" />
        </Link>
        <div className="w-8" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          {submitted ? (
            <div className="text-center space-y-6 rise-in">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <div className="space-y-2">
                <h1 className="text-3xl md:text-4xl font-medium">Check your inbox</h1>
                <p className="text-lg text-muted-foreground">
                  If an account exists for that email, a reset link has been sent.
                </p>
              </div>
              <Link to="/login" className="inline-block font-semibold text-foreground underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <div className="space-y-8 rise-in">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl font-medium">Reset your password</h1>
                <p className="text-lg text-muted-foreground">
                  Enter your email and we'll send instructions to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => { setEmail(event.target.value); if (error) setError(''); }}
                    placeholder="Email address"
                    aria-label="Email address"
                    className={inputClass}
                    autoComplete="email"
                    autoFocus
                  />
                  <InlineError className="mt-1.5 text-sm">{error}</InlineError>
                </div>

                <Button type="submit" disabled={loading} className="w-full h-14 text-lg">
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>

              <div className="text-center">
                <Link to="/login" className="font-semibold text-foreground underline">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
